import React, { useState } from 'react';
import { Message } from '../types';
import { Lang, t } from '../../../../i18n';

const TaskSuggestions: React.FC<{ 
  tasks: Message[], 
  selectedTaskId: string | null, 
  selectedTaskIds?: string[], // Array for multi-select (from parent)
  onSelectTask: (taskId: string | null) => void,
  onSelectMultipleTasks?: (taskIds: string[]) => void, // Callback for multi-select
  onContinue?: (selectedIds: string[]) => void, // Callback for continue button with selected IDs
  lang: Lang 
}> = ({ 
  tasks, 
  selectedTaskId, 
  selectedTaskIds = [],
  onSelectTask, 
  onSelectMultipleTasks,
  onContinue,
  lang 
}) => {
  const [page, setPage] = useState(0);
  const pageSize = 4;
  const totalPages = Math.ceil(tasks.length / pageSize);
  const start = page * pageSize;
  const end = start + pageSize;
  const currentTasks = tasks.slice(start, end);

  // Local selection state so clicks always have a visible effect,
  // even when the parent doesn't persist selectedTaskIds (regular task flow).
  const [localSelectedIds, setLocalSelectedIds] = useState<string[]>([]);

  // Always use multi-select mode:
  // - If parent provides selectedTaskIds (well-defined flow), they take precedence.
  // - Otherwise, fall back to our local state (regular task flow).
  const effectiveSelectedIds = selectedTaskIds.length > 0 
    ? selectedTaskIds 
    : (localSelectedIds.length > 0
        ? localSelectedIds
        : (selectedTaskId ? [selectedTaskId] : []));

  const handleTaskClick = (taskId: string) => {
    // Multi-select mode: toggle in array
    const newSelectedIds = effectiveSelectedIds.includes(taskId)
      ? effectiveSelectedIds.filter(id => id !== taskId)
      : [...effectiveSelectedIds, taskId];

    console.log('🟨 [TaskSuggestions] task bubble clicked', {
      taskId,
      prevSelected: effectiveSelectedIds,
      nextSelected: newSelectedIds,
    });

    // Let parent know (used by well-defined tasks), but don't rely on it
    // for UI feedback.
    if (onSelectMultipleTasks) {
      onSelectMultipleTasks(newSelectedIds);
    }

    // Always update local state so selection is visible immediately,
    // even when parent doesn't persist anything (regular task flow).
    setLocalSelectedIds(newSelectedIds);
  };

  // Always show all tasks (multi-select mode)
  const tasksToShow = currentTasks;

  return (
    <div className="task-suggestions-container">
      {tasksToShow.map((message) => {
        // Use taskId property directly if available, otherwise parse from text
        // Support both English and Dutch formats
        let taskId: string | null = null;
        let similarity: string = '1.0';
        
        if (message.taskId) {
          taskId = message.taskId;
        } else if (message.text) {
          // Try English format first
          const matchEn = message.text.match(/^Task ID: (.*)\nDescription: ([\s\S]*)\nSimilarity: (.*)$/);
          if (matchEn) {
            taskId = matchEn[1];
            similarity = matchEn[3];
          } else {
            // Try Dutch format
            const matchNl = message.text.match(/^Taak ID: (.*)\nBeschrijving: ([\s\S]*)\nGelijkenis: (.*)$/);
            if (matchNl) {
              taskId = matchNl[1];
              similarity = matchNl[3];
            }
          }
        }
        
        if (!taskId) {
          return null;
        }
        
        const isSelected = effectiveSelectedIds.includes(taskId);
        const description = message.taskDescription || '';
        
        // Get labels based on language
        const taskIdLabel = lang === 'nl' ? 'Taak ID:' : 'Task ID:';
        const similarityLabel = lang === 'nl' ? 'Gelijkenis:' : 'Similarity:';
        
        return (
          <button
            key={message.id}
            className={`task-suggestion-bubble ${isSelected ? 'task-selected' : ''}`}
            title={description}
            onClick={() => handleTaskClick(taskId!)}
          >
            <div className="task-bubble-header">
              <span className="task-bubble-id">{taskIdLabel} {taskId}</span>
              <span className="task-bubble-sim">{similarityLabel} {similarity}</span>
              {isSelected && (
                <span className="task-selection-indicator">✓</span>
              )}
            </div>
            <div className="task-bubble-desc">{description}</div>
          </button>
        );
      })}
      {/* Show Submit button when at least one option is selected */}
      {effectiveSelectedIds.length > 0 && onContinue && (
        <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={() => {
              console.log('🟦 [TaskSuggestions] INDienen BUTTON CLICKED', {
                selectedTaskIds: effectiveSelectedIds,
                selectedCount: effectiveSelectedIds.length,
                timestamp: new Date().toISOString()
              });
              onContinue(effectiveSelectedIds);
            }}
            className="task-submit-button"
          >
            {t(lang, 'submit')} ({effectiveSelectedIds.length})
          </button>
        </div>
      )}
      {effectiveSelectedIds.length === 0 && totalPages > 1 && (
        <div className="task-suggestions-arrows">
          <button
            className="task-arrow"
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            aria-label="Previous suggestions"
          >&#8592;</button>
          <button
            className="task-arrow"
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={page === totalPages - 1}
            aria-label="Next suggestions"
          >&#8594;</button>
        </div>
      )}
    </div>
  );
};

export default TaskSuggestions; 